import {defineStore} from "pinia";
import {api} from '@/api/client';
import type { AuthenticatedUserInterface, UserInterface } from '@/types/user'
import { handleErrorResponse } from '@/appdata/globals'
import type { TripInterface } from '@/types/trips'

const CONSTANT = {
    KEY_SUPER_USER: 'ROLE_SUPER_USER',
    KEY_ADMIN: 'ROLE_ADMIN',
    KEY_SALES_TEAM: 'ROLE_SALES',
    KEY_OPS_TEAM: 'ROLE_OPERATIONS',
    KEY_DATA_MANAGER: 'ROLE_DATA_ENTRY',
};

export const useAuthStore = defineStore('authData', {
    state: () => ({
        authenticated: false as boolean,
        locale: 'en-US' as string,
        user: null as AuthenticatedUserInterface | null,
    }),
    getters: {
        isSuperUser(state) {
            if (!state.user) return false;

            return state.user?.roles?.filter(role => {
                return role.aclKey === CONSTANT.KEY_SUPER_USER;
            }).length === 1;
        },
        isAdmin(state) {
            if (!state.user) return false;

            return state.user?.roles?.filter(role => {
                return role.aclKey === CONSTANT.KEY_ADMIN;
            }).length === 1;
        },
        isSalesTeam(state) {
            if (!state.user) return false;

            return state.user?.roles?.filter(role => {
                return role.aclKey === CONSTANT.KEY_SALES_TEAM;
            }).length === 1;
        },
        isOpsTeam(state) {
            if (!state.user) return false;

            return state.user?.roles?.filter(role => {
                return role.aclKey === CONSTANT.KEY_OPS_TEAM;
            }).length === 1;
        },
        isDataManager(state) {
            if (!state.user) return false;

            return state.user?.roles?.filter(role => {
                return role.aclKey === CONSTANT.KEY_DATA_MANAGER;
            }).length === 1;
        },
        userEnabled(state): boolean {
            // console.debug(state.user);
            return (state.user?.roles?.length ?? 0) > 0
        },
    },
    actions: {
      async fetchData() {
            console.log("AuthDataStore | fetchData");
            return await api.get("/v1/user")
                .then((response) => {
                    this.$patch({
                        authenticated: response.data.authenticated,
                        user: response.data.user,
                    });

                    return response.data.user;
                })
              .catch((error) => { handleErrorResponse(error) });
        },

      allowItineraryChanges(trip: TripInterface): boolean {
        if (this.isAdmin || this.isSuperUser) {
          return true;
        }
        else if (trip?.owner?.uuid === this.user?.uuid) {
          return true;
        }
        else if (this.isSalesTeam || this.isOpsTeam) {
          let accessControl = false;
          if (this.isSalesTeam) {
            const exceptions = trip?.accessExceptions?.filter((user: UserInterface) => {
              return this.user?.uuid === user.uuid;
            });

            accessControl = ((exceptions?.length ?? 0) > 0)
          }

          if (this.isOpsTeam) {
            accessControl = accessControl || (trip?.currentVersion?.final ?? trip?.status.depositPaid ?? false);
          }

          return accessControl;
        }

        return false;
      },
      allowPropertyAccess(trip: TripInterface, propertyName: string): boolean {
        const salesTeamProperties = [
          'depositPaid','final','excludePricing','whiteLabel',
          'commissionPercent','proposalTotal',
          'revision',
        ];

        if (this.isAdmin || this.isSuperUser) {
          return true;
        }
        else if (trip?.owner?.uuid === this.user?.uuid) {
          return true;
        }
        else if (salesTeamProperties.includes(propertyName)) {
          if (this.isSalesTeam) {
            const exceptions = trip?.accessExceptions?.filter((user: UserInterface) => {
              return this.user?.uuid === user.uuid;
            });

            return ((exceptions?.length ?? 0) > 0)
          }
        }

        return false;
      }
    }
});
